// frontend/src/pages/Methodology.tsx
// FIXED VERSION - Handles local images and better error handling

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Article as WordIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Language as WebIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';
// ==================== INTERFACES ====================

interface MethodologyDocument {
  id: number;
  title: string;
  description: string;
  content?: string;
  content_type?: string;
  filename?: string;
  file_path?: string;
  file_type?: string;
  file_size?: number;
  created_at?: string;
  updated_at?: string;
}

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
}

// ==================== CONTENT SANITIZATION ====================

// Remove local file references and clean content
const sanitizeContent = (html: string): string => {
  // First pass with DOMPurify
  let cleaned = DOMPurify.sanitize(html);
  
  // Remove local file:// references
  cleaned = cleaned.replace(/file:\/\/\/[^"'\s>]*/gi, '');
  
  // Remove img tags with empty or local src
  cleaned = cleaned.replace(/<img[^>]*src=['"]file:\/\/[^'"]*['"][^>]*>/gi, '');
  cleaned = cleaned.replace(/<img[^>]*src=['"]["']*>/gi, '');
  
  // Remove broken image tags
  cleaned = cleaned.replace(/<img[^>]*src=['"]['"][^>]*>/gi, '');
  
  // Clean up extra whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
};

// Validate content size (max 1MB of text)
const validateContentSize = (content: string): boolean => {
  const sizeInBytes = new Blob([content]).size;
  const maxSize = 1024 * 1024; // 1MB
  return sizeInBytes <= maxSize;
};

// ==================== REACT QUILL CONFIGURATION ====================

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link'],  // Removed 'image' and 'video' to prevent local file issues
    ['clean']
  ],
  clipboard: {
    // Strip local file references on paste
    matchVisual: false
  }
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link'  // Removed 'image' and 'video'
];

// ==================== MAIN COMPONENT ====================

const Methodology: React.FC = () => {
  // ===== STATE MANAGEMENT =====
  const [documents, setDocuments] = useState<MethodologyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [contentWarning, setContentWarning] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<MethodologyDocument | null>(null);
  
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [contentType, setContentType] = useState<'web' | 'file'>('web');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewMode, setPreviewMode] = useState<'edit' | 'preview'>('edit');
  
  // User role checking
  const [isAdmin, setIsAdmin] = useState(false);

  // ===== LOAD USER ROLE =====
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user: User = JSON.parse(userStr);
        setIsAdmin(user.role === 'admin');
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  // ===== FETCH DOCUMENTS =====
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/content/methodology`);
      setDocuments(response.data);
    } catch (err: any) {
      console.error('Error fetching methodology documents:', err);
      setError('Failed to load methodology documents');
    } finally {
      setLoading(false);
    }
  };

  // ===== HANDLE CONTENT CHANGE =====
  const handleContentChange = (value: string) => {
    setHtmlContent(value);
    setContentWarning(null);
    
    // Check for local file references
    if (value.includes('file:///')) {
      setContentWarning('Warning: Local images detected. They will be removed automatically. Please use web URLs for images.');
    }
    
    // Check content size
    if (!validateContentSize(value)) {
      setContentWarning('Warning: Content is too large (max 1MB). Please reduce the amount of content.');
    }
  };

  // ===== CREATE NEW DOCUMENT =====
  const handleCreateDocument = async () => {
    try {
      // Validation
      if (!title.trim()) {
        setError('Title is required');
        return;
      }

      if (contentType === 'web' && !htmlContent.trim()) {
        setError('Content is required for web pages');
        return;
      }

      if (contentType === 'file' && !selectedFile) {
        setError('Please select a file to upload');
        return;
      }

      setError(null);

      if (contentType === 'web') {
        // Clean and sanitize content
        const cleaned = sanitizeContent(htmlContent);
        
        // Validate size after cleaning
        if (!validateContentSize(cleaned)) {
          setError('Content is too large (max 1MB). Please reduce the amount of text.');
          return;
        }

        // Check if content is empty after cleaning
        if (!cleaned || cleaned.trim().length === 0) {
          setError('Content cannot be empty');
          return;
        }

        console.log('Sending content:', {
          title,
          description,
          contentLength: cleaned.length,
          contentPreview: cleaned.substring(0, 100)
        });

        const response = await axios.post(`${API_URL}/content/methodology`, {
          title: title.trim(),
          description: description.trim(),
          content: cleaned,
          content_type: 'web'
        });

        console.log('Server response:', response.data);
        setSuccess('Methodology page created successfully!');
      } else {
        // Upload file
        const formData = new FormData();
        formData.append('title', title.trim());
        formData.append('description', description.trim());
        formData.append('file', selectedFile!);

        await axios.post(`${API_URL}/content/methodology/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setSuccess('File uploaded successfully!');
      }

      // Reset form and close dialog
      resetForm();
      setCreateDialogOpen(false);
      fetchDocuments();

    } catch (err: any) {
      console.error('Error creating document:', err);
      console.error('Error response:', err.response?.data);
      
      // Show detailed error message
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error
        || err.message 
        || 'Failed to create document';
      
      setError(`Error: ${errorMessage}`);
    }
  };

  // ===== DELETE DOCUMENT =====
  const handleDeleteDocument = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/content/methodology/${id}`);
      setSuccess('Document deleted successfully');
      fetchDocuments();
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    }
  };

  // ===== VIEW DOCUMENT =====
  const handleViewDocument = (doc: MethodologyDocument) => {
    setSelectedDocument(doc);
    setViewDialogOpen(true);
  };

  // ===== DOWNLOAD FILE =====
  const handleDownloadFile = async (doc: MethodologyDocument) => {
    if (!doc.file_path) return;

    try {
      const response = await axios.get(
        `${API_URL}/content/methodology/${doc.id}/download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  // ===== HELPER FUNCTIONS =====
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setHtmlContent('');
    setSelectedFile(null);
    setContentType('web');
    setPreviewMode('edit');
    setError(null);
    setContentWarning(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return <DescriptionIcon />;
    if (fileType.includes('pdf')) return <PdfIcon />;
    if (fileType.includes('word') || fileType.includes('document')) return <WordIcon />;
    return <DescriptionIcon />;
  };

  // Sanitized HTML content for preview
  const sanitizedPreviewContent = useMemo(() => {
    return sanitizeContent(htmlContent);
  }, [htmlContent]);

  // Get content size for display
  const getContentSize = () => {
    if (!htmlContent) return '0 KB';
    const sizeInBytes = new Blob([htmlContent]).size;
    const sizeInKB = sizeInBytes / 1024;
    if (sizeInKB < 1024) return `${sizeInKB.toFixed(1)} KB`;
    return `${(sizeInKB / 1024).toFixed(2)} MB`;
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight={600} color="text.primary" gutterBottom>
            Methodology
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Technical documentation and methodological frameworks
          </Typography>
        </Box>
        
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' },
              textTransform: 'none',
              px: 3
            }}
          >
            Create New
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Documents Grid */}
      <Grid container spacing={3}>
        {documents.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#f8fafc' }}>
              <DescriptionIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No methodology documents yet
              </Typography>
              {isAdmin && (
                <Typography variant="body2" color="text.secondary">
                  Click "Create New" to add your first document
                </Typography>
              )}
            </Paper>
          </Grid>
        ) : (
          documents.map((doc) => (
            <Grid item xs={12} md={6} key={doc.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'white',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {doc.content_type === 'web' ? (
                        <WebIcon sx={{ color: '#2563eb' }} />
                      ) : (
                        getFileIcon(doc.file_type)
                      )}
                      <Chip 
                        label={doc.content_type === 'web' ? 'Web Page' : 'Document'} 
                        size="small"
                        sx={{
                          bgcolor: doc.content_type === 'web' ? '#dbeafe' : '#f1f5f9',
                          color: doc.content_type === 'web' ? '#1e40af' : '#475569',
                          fontWeight: 500
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                    {doc.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {doc.description}
                  </Typography>

                  {doc.content_type === 'file' && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {doc.filename} • {formatFileSize(doc.file_size)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <Divider />

                <CardActions sx={{ p: 2, gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDocument(doc)}
                    sx={{ textTransform: 'none', color: '#2563eb' }}
                  >
                    View
                  </Button>

                  {doc.content_type === 'file' && (
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadFile(doc)}
                      sx={{ textTransform: 'none', color: '#059669' }}
                    >
                      Download
                    </Button>
                  )}

                  {isAdmin && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteDocument(doc.id)}
                      sx={{ ml: 'auto', color: '#dc2626' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* ==================== CREATE DIALOG ==================== */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Create New Methodology Document
            </Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Content Type Selector */}
          <Box mb={3}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Content Type
            </Typography>
            <ToggleButtonGroup
              value={contentType}
              exclusive
              onChange={(e, value) => value && setContentType(value)}
              fullWidth
            >
              <ToggleButton value="web">
                <WebIcon sx={{ mr: 1 }} />
                Web Page
              </ToggleButton>
              <ToggleButton value="file">
                <CloudUploadIcon sx={{ mr: 1 }} />
                Upload File
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Title */}
          <TextField
            label="Title"
            fullWidth
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Description */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 3 }}
          />

          {/* WEB CONTENT EDITOR */}
          {contentType === 'web' && (
            <>
              {/* Content Warning */}
              {contentWarning && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  {contentWarning}
                </Alert>
              )}

              {/* Content Size Info */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Content
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Size: {getContentSize()}
                  </Typography>
                  <ToggleButtonGroup
                    value={previewMode}
                    exclusive
                    onChange={(e, value) => value && setPreviewMode(value)}
                    size="small"
                  >
                    <ToggleButton value="edit">
                      <EditIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      Edit
                    </ToggleButton>
                    <ToggleButton value="preview">
                      <PreviewIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      Preview
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* Helper Text */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  <strong>Tips:</strong> Type or paste your content below. 
                  Local images will be automatically removed. Use web URLs for images (e.g., https://...).
                </Typography>
              </Alert>

              {/* React Quill Editor */}
              {previewMode === 'edit' ? (
                <Box sx={{ 
                  '& .quill': {
                    bgcolor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: 1
                  },
                  '& .ql-toolbar': {
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0'
                  },
                  '& .ql-container': {
                    minHeight: 400,
                    fontSize: '14px'
                  }
                }}>
                  <ReactQuill
                    value={htmlContent}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Start writing your methodology content here..."
                  />
                </Box>
              ) : (
                // Preview Mode
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    minHeight: 400,
                    maxHeight: 600,
                    overflow: 'auto',
                    bgcolor: 'white',
                    '& img': { maxWidth: '100%', height: 'auto' },
                    '& table': { borderCollapse: 'collapse', width: '100%' },
                    '& td, & th': { border: '1px solid #e2e8f0', p: 1 }
                  }}
                >
                  {sanitizedPreviewContent && sanitizedPreviewContent.trim() ? (
                    <div dangerouslySetInnerHTML={{ __html: sanitizedPreviewContent }} />
                  ) : (
                    <Typography color="text.secondary" fontStyle="italic">
                      No content to preview. Switch to Edit mode to start writing.
                    </Typography>
                  )}
                </Paper>
              )}
            </>
          )}

          {/* FILE UPLOAD */}
          {contentType === 'file' && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Upload Document
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: '#f8fafc',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f1f5f9' }
                }}
              >
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    Click to upload file
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    PDF or Word documents only
                  </Typography>
                </label>

                {selectedFile && (
                  <Box mt={2} p={2} bgcolor="white" borderRadius={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Selected: {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(selectedFile.size)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateDocument}
            disabled={!!contentWarning && contentWarning.includes('too large')}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' },
              textTransform: 'none'
            }}
          >
            Create Document
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== VIEW DIALOG ==================== */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedDocument && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={600}>
                  {selectedDocument.title}
                </Typography>
                <IconButton onClick={() => setViewDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" mt={1}>
                {selectedDocument.description}
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              {selectedDocument.content_type === 'web' ? (
                // Display HTML content
                <Box 
                  sx={{ 
                    '& img': { maxWidth: '100%', height: 'auto' },
                    '& table': { borderCollapse: 'collapse', width: '100%' },
                    '& td, & th': { border: '1px solid #e2e8f0', p: 1 },
                    '& pre': { bgcolor: '#f8fafc', p: 2, borderRadius: 1, overflow: 'auto' },
                    '& code': { bgcolor: '#f8fafc', px: 1, py: 0.5, borderRadius: 0.5 }
                  }}
                >
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: sanitizeContent(selectedDocument.content || '') 
                    }} 
                  />
                </Box>
              ) : (
                // Display file info
                <Box textAlign="center" py={4}>
                  {getFileIcon(selectedDocument.file_type)}
                  <Typography variant="h6" mt={2} mb={1}>
                    {selectedDocument.filename}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    {formatFileSize(selectedDocument.file_size)}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadFile(selectedDocument)}
                    sx={{
                      bgcolor: '#2563eb',
                      '&:hover': { bgcolor: '#1d4ed8' },
                      textTransform: 'none'
                    }}
                  >
                    Download Document
                  </Button>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default Methodology;